/**
 * AGENT LAB — CodeAI101 chapter 4, who is driving the iteration.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 4.
 *
 * ── YOU BE THE LOOP FIRST ─────────────────────────────────────────────────────────────────────
 * §1: "The AI does one thing per turn. You are the loop." So the left pane makes the learner press
 * next, and next, and next — one turn per press, seven presses to get anywhere. Then the right pane
 * takes the loop away and does the same seven in one click. The difference between the two shapes
 * is felt in the fingers before it is understood, and that is cheaper than another paragraph
 * explaining it.
 *
 * ── THE BOUNDARIES ARE SWITCHES, AND TURNING ONE OFF IS THE POINT ─────────────────────────────
 * §5's four. Turn off the goal and the loop wanders; turn off the caps and it wanders expensively.
 * The lab prints his verdict AND the call count together, because with a vague goal and tight caps
 * it stops at twelve calls — cheap by any measure — while his rule read literally says
 * "expensively". Both are shown so the learner holds the distinction rather than distrusting one
 * of them: a goal decides whether it can finish, the caps decide what it costs when it cannot.
 *
 * ── NO PRICE IS INVENTED ──────────────────────────────────────────────────────────────────────
 * The module gives no per-call figure, so the learner supplies one. Leave it blank and no money
 * appears at all. A lab that printed a rupee rate would be inventing a commercial fact.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import Anchor from './anchor.jsx';
import {
  SHAPES, DIAL, BOUNDS, KIND_OF, runAgent, verdictFor, LAB_CEILING, DEFAULT_ATTEMPTS,
  THE_LINE, WHO_DRIVES, YOU_ARE_THE_LOOP, AI_IS_THE_LOOP, CALIBRATION, WHERE_MOST_LIVE,
  WHY_IT_FAILS, CHEAP_OR_EXPENSIVE, HIS_DEFAULT, SOURCE_1, SOURCE_3, SOURCE_5,
} from './agentloop.mjs';

export default function AgentLab() {
  const [turns, setTurns] = useState(0);            // the human loop
  const [bounds, setBounds] = useState({ goal: true, attempts: true, cost: true, time: true });
  const [price, setPrice] = useState('');
  const [ran, setRan] = useState(null);
  const [dial, setDial] = useState('monitored');

  const p = price.trim() === '' ? null : Number(price);
  const go = () => setRan(runAgent({ bounds, pricePerCall: Number.isFinite(p) ? p : null }));
  const toggle = (id) => { setBounds((b) => ({ ...b, [id]: !b[id] })); setRan(null); };

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Agent lab · who is driving</div>
      <h2 className="g-h2 lab-h">You are the loop. Until you are not.</h2>
      <Anchor chapter={4} />
      <p className="g-body"><Value of={cited(THE_LINE, SOURCE_1)} /></p>

      <div className="agt-two">
        {/* ── YOU DRIVE ── */}
        <div className="agt-pane">
          <div className="g-label">{SHAPES.prompt.name} — {SHAPES.prompt.driver} drive</div>
          <code className="agt-arrow">{SHAPES.prompt.arrow}</code>
          <div className="agt-turns">
            {Array.from({ length: turns }, (_, i) => (
              <div key={i} className="agt-turn"><span className="cou-n">{i + 1}</span> you asked · it answered · you read it</div>
            ))}
            {turns === 0 && <p className="g-body g-dim">Nothing has happened. Nothing will, until you press.</p>}
          </div>
          <div className="lab-task-head">
            <button className="g-btn g-btn-primary" onClick={() => setTurns((t) => t + 1)}>Next turn</button>
            <button className="g-btn" onClick={() => setTurns(0)}>Reset</button>
            <span className="cou-ms">{turns} press{turns === 1 ? '' : 'es'}</span>
          </div>
          <p className="g-body g-dim agt-note"><Value of={cited(YOU_ARE_THE_LOOP, SOURCE_1)} /></p>
        </div>

        {/* ── IT DRIVES ── */}
        <div className="agt-pane">
          <div className="g-label">{SHAPES.agent.name} — {SHAPES.agent.driver} drives</div>
          <code className="agt-arrow">{SHAPES.agent.arrow}</code>
          <div className="agt-turns">
            {ran ? ran.trace.slice(0, 30).map((t) => (
              <div key={t.call} className={`agt-turn ${t.note === 'goal reached' ? 'agt-hit' : ''}`}>
                <span className="cou-n">{t.call}</span> {t.note}
              </div>
            )) : <p className="g-body g-dim">One press and it runs its own loop.</p>}
            {ran && ran.trace.length > 30 && <div className="agt-more">… {ran.trace.length - 30} more</div>}
          </div>
          <div className="lab-task-head">
            <button className="g-btn g-btn-primary" onClick={go}>Set the goal and let go</button>
            <button className="g-btn" onClick={() => setRan(null)}>Reset</button>
            <span className="cou-ms">{ran ? `${ran.calls} calls` : '1 press'}</span>
          </div>
          <p className="g-body g-dim agt-note"><Value of={cited(AI_IS_THE_LOOP, SOURCE_1)} /></p>
        </div>
      </div>

      <p className="g-body agt-punch"><Value of={cited(WHO_DRIVES, SOURCE_1)} /></p>

      {/* ── THE FOUR BOUNDARIES ── */}
      <div className="lab-task">
        <div className="g-label">What stops it</div>
        <p className="g-body"><Value of={cited(WHY_IT_FAILS, SOURCE_5)} /></p>
        <div className="agt-bounds">
          {BOUNDS.map((b) => (
            <label key={b.id} className={`agt-bound ${bounds[b.id] ? '' : 'agt-off'}`}>
              <input type="checkbox" checked={bounds[b.id]} onChange={() => toggle(b.id)} />
              <b>{b.name}</b>
              <span className="agt-kind">{KIND_OF[b.id]}</span>
              <span className="g-body g-dim agt-q">{b.quote}</span>
            </label>
          ))}
        </div>
        <div className="lab-task-head">
          <span className="g-label">Your price per call, if you want money on screen</span>
          <input className="morse-input agt-price" value={price} placeholder="e.g. 2"
            onChange={(e) => { setPrice(e.target.value); setRan(null); }} />
          <span className="g-body g-dim">the module gives no rate, so this one is yours</span>
        </div>

        {ran && (
          <>
            <div className="lab-readout">
              <div><span className="g-label">Tool calls</span><Value of={computed(String(ran.calls))} /></div>
              <div><span className="g-label">Stopped by</span><span className="lab-num">{ran.stoppedBy}</span></div>
              <div><span className="g-label">Cost</span>
                {ran.cost == null ? <Value of={absent()} /> : <Value of={computed(String(ran.cost))} />}</div>
              <div><span className="g-label">Verdict</span>
                <span className="lab-num">{verdictFor(ran, bounds)}</span></div>
            </div>
            {ran.labStopped && (
              <p className="g-body agt-ceiling">
                <b>This page stopped it at {LAB_CEILING} calls.</b> Nothing in the scenario did — every
                boundary was off. A real agent here would still be going, and the only thing that
                ends it is somebody noticing. The ceiling is the lab protecting your browser, not a
                safety net that exists in production.
              </p>
            )}
            {!ran.succeeded && !ran.labStopped && (
              <p className="g-body agt-stopped">
                It stopped because of the <b>{ran.stoppedBy}</b> limit — it did not finish. A limit
                reached is a report back, not a result.
              </p>
            )}
            <p className="g-body g-dim"><Value of={cited(CHEAP_OR_EXPENSIVE, SOURCE_5)} /></p>
            {!bounds.goal && ran.calls < 20 && (
              <p className="g-body g-dim agt-nuance">
                Note the tension worth holding: this wandered and still stopped in {ran.calls} calls,
                because the caps held. His rule calls any missing boundary “expensive”, and the count
                says otherwise — because a goal decides whether it can <i>finish</i>, and the other
                three decide what it <i>costs</i> when it cannot.
              </p>
            )}
          </>
        )}
        <p className="g-body g-dim"><Value of={cited(HIS_DEFAULT, SOURCE_5)} /> This lab starts at {DEFAULT_ATTEMPTS}.</p>
      </div>

      {/* ── THE DIAL ── */}
      <div className="lab-task">
        <div className="g-label">Where on the dial is this product?</div>
        <div className="lab-task-head">
          {DIAL.map((d) => (
            <button key={d.id} className={`g-btn ${dial === d.id ? 'g-btn-primary' : ''}`}
              onClick={() => setDial(d.id)}>{d.n}. {d.name}</button>
          ))}
        </div>
        <p className="g-body"><Value of={cited(DIAL.find((d) => d.id === dial).quote, SOURCE_3)} /></p>
        <p className="g-body"><Value of={cited(CALIBRATION, SOURCE_3)} /></p>
        <p className="g-body g-dim"><Value of={cited(WHERE_MOST_LIVE, SOURCE_3)} /></p>
      </div>

      <p className="g-body g-dim lab-why">
        No model runs and no money is real. The module gives no per-call price — it says a confused
        agent can burn “hundreds of rupees … in minutes” and leaves the rate alone, because it varies
        by model and tool — so the figure above is one you typed. Leave it blank and no money appears.
        <br />
        The loop here cannot exceed {LAB_CEILING} calls whatever you switch off. A lab about
        unterminating loops that could hang your browser would be the joke that writes itself — but
        that ceiling is this page defending itself, and it is not a thing production has.
      </p>
    </div>
  );
}
